import { Box, Flex, Stack } from '@chakra-ui/react';
import { Avatar, LoadingState, Text } from '@main/complykit';
import { Header, PrimaryLayout, SidebarMenu } from '@main/ui';
import { useAuthenticationStatus, useUserEmail } from '@nhost/react';
import { Outlet } from '@tanstack/react-router';
import { useEffect } from 'react';

import { useVendorAppDispatch, useVendorAppSelector } from '../../hooks/redux-toolkit-hooks';
import { getVendorUserState, vendorPageLoaded } from '../vendor-questionnaires/slice';
import { useVendorDrawer } from './vendor-drawer';
import { useVendorSearchParams, useVendorSidebarMenu } from './vendor-menu';

export const VendorLayout = () => {
  const dispatch = useVendorAppDispatch();
  const email = useUserEmail();
  const { isLoading, isAuthenticated } = useAuthenticationStatus();
  const vendorUserState = useVendorAppSelector(getVendorUserState);
  const { vendorToken } = useVendorSearchParams();
  const { menuItems } = useVendorSidebarMenu();
  const drawerContent = useVendorDrawer();
  const companyName = email?.split('@')[1] || 'Vendor portal';

  useEffect(() => {
    dispatch(vendorPageLoaded({ vendorToken }));
    // oxlint-disable-next-line react/exhaustive-deps
  }, []);

  if (!isAuthenticated && vendorUserState === 'errored') {
    return null;
  }

  if (isLoading || !isAuthenticated || vendorUserState !== 'ready') {
    return (
      <Stack height="100vh" align="center" justify="center" shouldWrapChildren>
        <LoadingState />
      </Stack>
    );
  }

  return (
    <PrimaryLayout
      logoLink="/vendor/$vendorToken"
      sidebar={({ isSidebarCollapsed, onSidebarClose }) => (
        <Flex h="full" direction="column">
          <Box overflowY="auto" flex={1} mb={4}>
            <Box px={3} py={5}>
              <Flex minWidth={0} grow={1} alignItems="center" gap={3}>
                <Avatar name={companyName} size="md" shape="square" isDecorative />
                <Flex
                  direction="column"
                  grow={1}
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  overflow="hidden"
                >
                  <Text size="sm" weight="semibold" color="inherit" isTruncated>
                    {companyName}
                  </Text>
                </Flex>
              </Flex>
            </Box>
            <SidebarMenu
              items={menuItems}
              onClick={onSidebarClose}
              isSidebarCollapsed={isSidebarCollapsed}
            />
          </Box>
        </Flex>
      )}
      header={({ logo, onSidebarOpen }) => (
        <Header
          logo={logo}
          onSidebarToggle={onSidebarOpen}
          menuContent={
            <Flex alignItems="center">
              <Avatar name={email ?? ''} size="md" />
              <Box flexGrow={1} px={2} display={{ base: 'none', md: 'block' }}>
                <Text size="xs" color="muted">
                  {email}
                </Text>
              </Box>
            </Flex>
          }
        />
      )}
      drawer={drawerContent}
    >
      <Outlet />
    </PrimaryLayout>
  );
};
